// Pit-Stop-Finder — "ich hab Hunger/Durst/muss zu Decathlon": findet POIs einer
// Kategorie, die in FAHRTRICHTUNG voraus liegen (nie zurückradeln!) und den
// geringsten Umweg kosten.
//
// "Voraus" = die Peilung vom Standort zum POI liegt innerhalb eines Kegels um
// die aktuelle Fahrtrichtung (GPS-heading). POIs hinter dem Fahrer fallen raus.
// Ranking = Distanz, leicht bestraft je weiter der POI seitlich/schräg liegt
// (geradeaus + nah = bester Pit-Stop).
//
// Reine Funktionen ohne Abhängigkeiten zu DOM/Map → unit-testbar.

import { haversine } from '@adtu/shared';
import type { POI } from './poi';

// Such-Radius für "Nächster Stopp voraus" — im Profil/Admin einstellbar
// (10–50 km). Kleiner = weniger Kandidaten + kürzere Umweg-Routings = schneller.
export const PITSTOP_RADIUS_PREF = 'adt_pitstop_radius_km';
export const PITSTOP_RADIUS_DEFAULT_KM = 30;
export const PITSTOP_RADIUS_MIN_KM = 10;
export const PITSTOP_RADIUS_MAX_KM = 50;

export interface PitstopCandidate {
  poi: POI;
  /** Luftlinie Standort→POI in Metern. */
  distM: number;
  /** Peilung Standort→POI (0=N, im Uhrzeigersinn). */
  bearingDeg: number;
  /** Winkel zwischen Fahrtrichtung und Peilung (0 = exakt voraus, 180 = hinter). */
  offAngleDeg: number;
}

export interface ForwardOpts {
  /** Halber Öffnungswinkel des "Voraus"-Kegels. Default 100° (±100° = vorne +
   *  seitlich; alles dahinter gilt als "zurück" und fällt raus). */
  maxConeDeg?: number;
  /** Maximale Luftlinie (km), darüber ignorieren. Default 60. */
  maxDistKm?: number;
  /** Wie viele Treffer. Default 5. */
  limit?: number;
}

/** Peilung von (aLat,aLon) nach (bLat,bLon) in Grad, 0=Nord, im Uhrzeigersinn. */
export function bearingDeg(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const toRad = (d: number) => (d * Math.PI) / 180;
  const φ1 = toRad(aLat);
  const φ2 = toRad(bLat);
  const Δλ = toRad(bLon - aLon);
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  const θ = Math.atan2(y, x);
  return (θ * 180) / Math.PI; // -180..180
}

/** Kleinster absoluter Winkelabstand zweier Kompasswinkel (0..180). */
export function angleDiff(a: number, b: number): number {
  let d = Math.abs(((a - b) % 360 + 360) % 360);
  if (d > 180) d = 360 - d;
  return d;
}

/**
 * POIs in Fahrtrichtung voraus, sortiert nach geringstem Umweg.
 * @param origin Standort + Fahrtrichtung (heading in Grad; NaN/null wenn unbekannt).
 */
export function forwardPois(
  origin: { lat: number; lon: number; heading: number | null | undefined },
  pois: POI[],
  opts: ForwardOpts = {},
): PitstopCandidate[] {
  const cone = opts.maxConeDeg ?? 100;
  const maxD = (opts.maxDistKm ?? 60) * 1000;
  const limit = opts.limit ?? 5;
  const heading = origin.heading;
  const hasHeading = typeof heading === 'number' && Number.isFinite(heading);

  const cand: PitstopCandidate[] = [];
  for (const p of pois) {
    if (typeof p.lat !== 'number' || typeof p.lon !== 'number') continue;
    const distM = haversine(origin.lat, origin.lon, p.lat, p.lon);
    if (distM < 1 || distM > maxD) continue;
    const brg = bearingDeg(origin.lat, origin.lon, p.lat, p.lon);
    const off = hasHeading ? angleDiff(brg, heading) : 0;
    // Nur voraus: alles außerhalb des Kegels (= hinter dem Fahrer) raus.
    if (hasHeading && off > cone) continue;
    cand.push({ poi: p, distM, bearingDeg: brg, offAngleDeg: off });
  }

  // Score: Distanz, plus Strafe je schräger der POI liegt (1.0 geradeaus →
  // bis ~2.0 bei 90° seitlich). Geradeaus + nah gewinnt.
  const score = (c: PitstopCandidate) => c.distM * (1 + c.offAngleDeg / 90);
  cand.sort((a, b) => score(a) - score(b));
  return cand.slice(0, limit);
}

/**
 * Fahrtrichtung entlang einer geplanten Route am aktuellen Standort.
 * Sucht den nächstgelegenen Routenpunkt und peilt zum nächsten Punkt VORAUS
 * (Route ist Start→Ziel geordnet). So kommt die "Voraus"-Richtung bei aktivem
 * GPX-Ride automatisch aus der Route — ohne manuelles Heading.
 * @param route [lat, lon]-Punkte (wie routeBus liefert). null/zu kurz → null.
 */
export function routeBearingAt(
  route: Array<[number, number, number?]> | null | undefined,
  lat: number,
  lon: number,
): number | null {
  if (!route || route.length < 2) return null;
  let bestIdx = 0;
  let bestD = Infinity;
  for (let i = 0; i < route.length; i++) {
    const pt = route[i];
    if (!pt) continue;
    const d = haversine(lat, lon, pt[0], pt[1]);
    if (d < bestD) {
      bestD = d;
      bestIdx = i;
    }
  }
  const a = bestIdx < route.length - 1 ? route[bestIdx] : route[bestIdx - 1];
  const b = bestIdx < route.length - 1 ? route[bestIdx + 1] : route[bestIdx];
  if (!a || !b) return null;
  return bearingDeg(a[0], a[1], b[0], b[1]);
}

/** Kompass-Himmelsrichtung (N, NE, …) für ein UI-Label. */
export function compassLabel(deg: number): string {
  const dirs = ['N', 'NE', 'E', 'SE', 'S', 'SW', 'W', 'NW'];
  return dirs[Math.round((((deg % 360) + 360) % 360) / 45) % 8] ?? 'N';
}
